import { hoverHelp } from "./hoverHelp.ts";
import type { InventoryListSortColumn, InventoryListSortState } from "./inventoryListSort.ts";
export type InventoryTableMode = "catalog" | "stock";
export interface InventoryRowPresentation {
  mode: InventoryTableMode;
  ids: { row: string; name: string; package: string; onHand: string; par: string };
  productId: string;
  name: string;
  stopped: boolean;
  flagged: boolean;
  packageText: string;
  sizeNeedsConfirmation: boolean;
  negative: boolean;
  onHand: string;
  par: string;
  noPar: boolean;
  extraCells: readonly HTMLElement[];
  /** BULKEDIT1: the checkbox cell, drawn first (Items list only). */
  selectCell?: HTMLElement;
  /** BULKEDIT1: cells drawn right after the item cell, before On hand (Items list: Vendor, Shelf tier, Status). */
  midCells?: readonly HTMLElement[];
  openRow: () => void;
}
export function createInventoryRow(model: InventoryRowPresentation): HTMLElement {
  const { mode, ids, openRow } = model;
  const row = document.createElement("tr");
  row.className = model.flagged
    ? "admin-bottle-row inv-row inv-row--86"
    : "admin-bottle-row inv-row";
  row.dataset["testid"] = ids.row;
  row.dataset["liquorProductId"] = model.productId;
  row.tabIndex = 0;

  if (model.selectCell !== undefined) {
    // The row opens the item editor on click and on Enter or Space; the checkbox cell must not.
    model.selectCell.addEventListener("click", (event) => event.stopPropagation());
    model.selectCell.addEventListener("keydown", (event) => event.stopPropagation());
    row.append(model.selectCell);
  }
  const nameCell = document.createElement("td");
  nameCell.className = "inv-item-name";
  // Truncated to one line (styles.css's .admin-bottle-name-text); not a
  // title-attribute-only recovery (CLAUDE.md's touch-only rule forbids that):
  // activating the row opens the sheet with the complete, untruncated name.
  const nameText = document.createElement("span");
  nameText.className = "admin-bottle-name-text";
  nameText.textContent = model.name;
  nameText.dataset["testid"] = ids.name;
  nameCell.append(nameText);
  if (model.stopped) {
    const stoppedTag = document.createElement("span");
    stoppedTag.className = "inv-locations-tag";
    stoppedTag.textContent = "Stop ordering";
    nameCell.append(stoppedTag);
  }
  row.append(nameCell);
  if (model.midCells !== undefined) row.append(...model.midCells);

  const packageCell = document.createElement(mode === "catalog" ? "div" : "td");
  packageCell.className = "inv-col-package";
  packageCell.dataset["testid"] = ids.package;
  packageCell.textContent = model.packageText;
  if (model.sizeNeedsConfirmation) {
    // P11-F1 (DEC-372): the size is an assumed 750 mL Bottle until a manager confirms it.
    const assumedTag = document.createElement("span");
    assumedTag.className = "inv-size-assumed";
    assumedTag.dataset["testid"] = "admin-size-assumed-tag";
    assumedTag.textContent = "Confirm size";
    packageCell.append(assumedTag);
  }
  if (mode === "catalog") nameCell.append(packageCell);
  else row.append(packageCell);

  const onHandCell = document.createElement("td");
  onHandCell.className = model.negative ? "inv-col-onhand negative" : "inv-col-onhand";
  onHandCell.dataset["testid"] = ids.onHand;
  onHandCell.textContent = model.onHand;
  row.append(onHandCell);

  const parCell = document.createElement("td");
  parCell.className = "inv-col-par";
  parCell.dataset["testid"] = ids.par;
  parCell.textContent = model.par;
  if (model.noPar) parCell.classList.add("inv-col-par--none");
  row.append(parCell);
  row.append(...model.extraCells);
  if (mode === "catalog") {
    for (const cell of [parCell, ...model.extraCells]) {
      const stopControl = (event: Event): void => {
        if (event.target instanceof Element && event.target.closest("button") !== null)
          event.stopPropagation();
      };
      cell.addEventListener("click", stopControl);
      cell.addEventListener("keydown", stopControl);
    }
  }
  row.addEventListener("click", openRow);
  // CLAUDE.md: browser-only, touch-first, but keyboard and pointer both
  // reachable (the catalog is an admin surface). tabIndex above makes the row
  // focusable; Enter/Space activates it, the same convention the sort header
  // buttons below use natively as real <button> elements.
  row.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openRow();
    }
  });
  return row;
}

export interface InventoryHeaderPresentation {
  mode: InventoryTableMode;
  ids: { sortPrefix: string };
  sort: InventoryListSortState;
  withStock: boolean;
  stockUnavailable: boolean;
  columns: readonly { column: InventoryListSortColumn; label: string; width: string }[];
  onSort: (column: InventoryListSortColumn) => void;
  /** BULKEDIT1: the select-all-shown checkbox, drawn first (Items list only). */
  selectAll?: { width: string; checked: boolean; indeterminate: boolean; disabled: boolean; onToggle: (checked: boolean) => void };
  /** BULKEDIT1: plain headers drawn right after the item column (Items list: Vendor, Shelf tier, Status). */
  midHeads?: readonly { label: string; width: string }[];
  /** BULKEDIT1: widths of the Auto par, Used per week and Lock headers when the Items list is drawn with more columns. */
  metricWidths?: { auto: string; used: string; lock: string };
}
export function createInventoryHead(model: InventoryHeaderPresentation): HTMLElement {
  const { mode, ids, sort, withStock } = model;
  const thead = document.createElement("thead");
  const headRow = document.createElement("tr");
  if (model.selectAll !== undefined) {
    const selectAll = model.selectAll;
    const th = document.createElement("th");
    th.scope = "col";
    th.className = "inv-head-select";
    th.style.width = selectAll.width;
    const box = document.createElement("input");
    box.type = "checkbox";
    box.className = "inv-select-box";
    box.dataset["testid"] = "admin-bulk-edit-check-all";
    box.setAttribute("aria-label", "Select all shown");
    box.checked = selectAll.checked;
    box.indeterminate = selectAll.indeterminate;
    box.disabled = selectAll.disabled;
    box.addEventListener("change", () => selectAll.onToggle(box.checked));
    th.append(box);
    headRow.append(th);
  }
  let headIndex = 0;
  for (const { column, label, width } of model.columns) {
    const th = document.createElement("th");
    th.scope = "col";
    th.style.width = width;
    if (model.selectAll !== undefined && headIndex === 0) th.className = "inv-head-item";
    const isActive = sort.column === column;
    th.setAttribute(
      "aria-sort",
      isActive ? (sort.direction === "asc" ? "ascending" : "descending") : "none",
    );
    const button = document.createElement("button");
    button.type = "button";
    button.className = "inv-sort-btn";
    button.disabled =
      mode === "catalog" && (column === "onHand" || column === "par") && model.stockUnavailable;
    button.dataset["testid"] = `${ids.sortPrefix}${column}`;
    const caret = document.createElement("span");
    caret.className = "caret";
    caret.textContent = isActive ? (sort.direction === "asc" ? "▲" : "▼") : "⇅";
    button.append(`${label} `, caret);
    // INVUX1 (finding 18): Status is the quantity reading; an 86 shows here as a fact, not as a change to On hand.
    if (mode === "stock" && column === "status") {
      hoverHelp(
        button,
        "Read from On hand and par. It also shows 86'd when someone has marked the item.",
        "Marking or clearing 86 never changes On hand.",
        "An item can be 86'd with stock on the shelf, or Out and still not 86'd.",
      );
    }
    button.addEventListener("click", () => model.onSort(column));
    th.append(button);
    headRow.append(th);
    headIndex += 1;
    if (headIndex === 1) {
      for (const mid of model.midHeads ?? []) {
        const midTh = document.createElement("th");
        midTh.scope = "col";
        midTh.className = "inv-head-text";
        midTh.style.width = mid.width;
        const midLabel = document.createElement("span");
        midLabel.className = "inv-sort-btn inv-stock-header-label";
        midLabel.textContent = mid.label;
        midTh.append(midLabel);
        headRow.append(midTh);
      }
    }
  }
  if (mode === "catalog") {
    const widths = model.metricWidths ?? { auto: "21%", used: "15%", lock: "7%" };
    for (const [label, width, headClass] of [
      ["Auto par", widths.auto, "inv-head-auto"],
      ["Used per week", widths.used, "inv-head-used"],
      ["Lock", widths.lock, "inv-head-lock"],
    ] as const) {
      const th = document.createElement("th");
      th.scope = "col";
      th.className = `inv-numeric-header ${headClass}`;
      th.style.width = width;
      th.textContent = label;
      headRow.append(th);
    }
  }
  if (withStock) {
    // STK1: not sortable; the Status column already ranks 86'd first.
    const stockTh = document.createElement("th");
    stockTh.scope = "col";
    stockTh.style.width = "22%";
    stockTh.dataset["testid"] = "admin-stock-header";
    const stockLabel = document.createElement("span");
    stockLabel.className = "inv-sort-btn inv-stock-header-label";
    stockLabel.textContent = "Availability";
    hoverHelp(
      stockLabel,
      "Whether the item is offered to sell right now. Mark 86 and Clear 86 apply at once.",
      "They never change On hand, and an item with stock can still be 86'd.",
      "An item showing Out in Status is Not 86'd until someone marks it.",
    );
    stockTh.append(stockLabel);
    headRow.append(stockTh);
  }
  thead.append(headRow);
  return thead;
}

export function buildBottleGroupHeadingRow(
  label: string,
  count: number,
  testid: string,
  columnCount: number,
): HTMLElement {
  const row = document.createElement("tr");
  row.className = "admin-bottle-group-heading-row";
  // The testid lives on the <tr> itself, not the inner <td>: this row is
  // the direct child of <tbody>, and e2e/admin.spec.ts's own
  // bottleListOutline helper reads each direct child's OWN dataset.testid.
  row.dataset["testid"] = testid;
  const cell = document.createElement("td");
  cell.colSpan = columnCount;
  cell.className = "admin-bottle-group-heading";
  cell.textContent = `${label} (${count})`;
  row.append(cell);
  return row;
}

export function createInventoryTable(
  mode: InventoryTableMode,
  testId: string,
  head: HTMLElement,
): HTMLTableElement {
  const table = document.createElement("table");
  table.className = mode === "catalog" ? "inv-table inv-table--unified" : "inv-table";
  table.dataset["testid"] = testId;
  table.append(head);
  return table;
}
export const INVENTORY_LIST_COLUMNS: Record<
  InventoryTableMode,
  ReadonlyArray<{ column: InventoryListSortColumn; label: string; width: string }>
> = {
  catalog: [
    { column: "item", label: "Item / package", width: "32%" },
    { column: "onHand", label: "On hand", width: "13%" },
    { column: "par", label: "Par", width: "12%" },
  ],
  stock: [
    { column: "item", label: "Item", width: "25%" },
    { column: "package", label: "Package", width: "13%" },
    { column: "onHand", label: "On hand", width: "14%" },
    // INVLOC1-S2: wide enough for "No par set" at the narrowest admin width (820px portrait); it may also wrap.
    { column: "par", label: "Par", width: "12%" },
    { column: "status", label: "Status", width: "14%" },
  ],
};

export function createInventoryMetricCells(
  metrics: { auto: HTMLElement; used: HTMLElement; lock: HTMLElement } | undefined,
  unavailable: string,
): HTMLElement[] {
  const autoCell = document.createElement("td");
  autoCell.className = "inv-col-auto";
  const usedCell = document.createElement("td");
  usedCell.className = "inv-col-used";
  const lockCell = document.createElement("td");
  lockCell.className = "inv-col-lock";
  if (metrics !== undefined) {
    autoCell.append(metrics.auto);
    usedCell.append(metrics.used);
    lockCell.append(metrics.lock);
  } else {
    autoCell.textContent = "–";
    usedCell.textContent = "–";
    lockCell.textContent = "–";
    hoverHelp(
      autoCell,
      unavailable,
      "Shown in Inventory for the selected location.",
      "A fresh authorized inventory read provides the available quantities.",
    );
  }
  return [autoCell, usedCell, lockCell];
}
